import React, { useMemo, useRef, useState } from 'react';
import { BorderBeam } from 'border-beam';
import { useTranslation } from 'react-i18next';
import {
    Brush,
    ChevronDown,
    Expand,
    ImageUpscale,
    Loader2,
    Pencil,
    Play,
    SquareDashedMousePointer,
    Type,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

import { NodeShell } from './NodeShell';
import { CircleActionButton, SvgRemove, SvgDuplicate, SvgAgent } from './ProductNode';
import { ImagePreviewDialog } from '../components/ImagePreviewDialog';

// 320 fits the 3-column tool-picker grid (2 rows: Expand/Retouch/Modify
// on top, Graphics/Upscale on the bottom) with room for the longest
// one-word label ("GRAPHISMES" in French) on a single line per button.
export const EDIT_IMAGE_NODE_WIDTH = 320;

export type EditTool = 'expand' | 'retouch' | 'modify' | 'graphics' | 'upscale' | 'design';

// Per-tool readiness — true tools render a Settings card + enabled
// Generate button; false tools render a "coming soon" placeholder.
const TOOL_READY: Record<EditTool, boolean> = {
    expand: true,
    retouch: true,
    modify: true,
    graphics: true,
    upscale: true,
    design: true,
};

// 'graphics' is intentionally omitted — it's superseded by the Design tool
// (reached via the generation's Edit menu), which is the richer graphics/text
// canvas. The type + pipeline stay for back-compat with existing graphics nodes.
const TOOL_OPTIONS: EditTool[] = ['expand', 'retouch', 'modify', 'upscale', 'design'];

interface ToolMeta { label: string; hint: string; ready: boolean; }

interface ToolMetaFull extends ToolMeta { shortLabel: string; }

// i18n-aware tool metadata. Built per-render via the hook so label/hint
// follow the active locale. `shortLabel` is a one-word variant used in
// the compact tool-picker buttons (where long labels like "Modify by
// prompt" or "Ajouter des graphismes" get truncated and look broken).
function useToolMeta(): Record<EditTool, ToolMetaFull> {
    const { t } = useTranslation();
    return useMemo(() => ({
        expand: { label: t('CONTENT_STUDIO.EDIT_TOOL_EXPAND'), shortLabel: t('CONTENT_STUDIO.EDIT_TOOL_EXPAND_SHORT'), hint: t('CONTENT_STUDIO.EDIT_TOOL_EXPAND_HINT'), ready: TOOL_READY.expand },
        retouch: { label: t('CONTENT_STUDIO.EDIT_TOOL_RETOUCH'), shortLabel: t('CONTENT_STUDIO.EDIT_TOOL_RETOUCH_SHORT'), hint: t('CONTENT_STUDIO.EDIT_TOOL_RETOUCH_HINT'), ready: TOOL_READY.retouch },
        modify: { label: t('CONTENT_STUDIO.EDIT_TOOL_MODIFY'), shortLabel: t('CONTENT_STUDIO.EDIT_TOOL_MODIFY_SHORT'), hint: t('CONTENT_STUDIO.EDIT_TOOL_MODIFY_HINT'), ready: TOOL_READY.modify },
        graphics: { label: t('CONTENT_STUDIO.EDIT_TOOL_GRAPHICS'), shortLabel: t('CONTENT_STUDIO.EDIT_TOOL_GRAPHICS_SHORT'), hint: t('CONTENT_STUDIO.EDIT_TOOL_GRAPHICS_HINT'), ready: TOOL_READY.graphics },
        upscale: { label: t('CONTENT_STUDIO.EDIT_TOOL_UPSCALE'), shortLabel: t('CONTENT_STUDIO.EDIT_TOOL_UPSCALE_SHORT'), hint: t('CONTENT_STUDIO.EDIT_TOOL_UPSCALE_HINT'), ready: TOOL_READY.upscale },
        design: { label: t('CONTENT_STUDIO.EDIT_TOOL_DESIGN'), shortLabel: t('CONTENT_STUDIO.EDIT_TOOL_DESIGN_SHORT'), hint: t('CONTENT_STUDIO.EDIT_TOOL_DESIGN_HINT'), ready: TOOL_READY.design },
    }), [t]);
}

interface EditImageNodeProps {
    nodeId: string;
    x: number;
    y: number;
    zoom: number;
    selected?: boolean;
    tool: EditTool;
    /** Source image (parent generation) — shown as a preview on the node. */
    sourceImageUrl?: string;
    /** Pretty summary of the configured settings for the current tool. */
    settingsSummary?: string | null;
    /** Whether the generate job is in flight. */
    generating?: boolean;
    /** Credit cost to display on the generate button (current tool). */
    creditCost: number;
    creditBalance?: number | null;
    /** Retouch regions (selections) — listed under the preview on the node. */
    retouchRegions?: Array<{ id: string; prompt: string }>;
    /**
     * Folded to its name and a Generate button.
     *
     * Set on nodes a plan created, for the same reason recipes are: a plan that
     * chains three edits buries the pictures the vendor asked for under three
     * settings panels. One added by hand is a node being configured, so it opens.
     */
    collapsed?: boolean;
    onToggleCollapse?: () => void;
    /** Upscale scale factor (2 / 4), chosen inline on the node. */
    upscaleScale?: number;
    onUpscaleScaleChange?: (scale: number) => void;
    /** Modify tool inline state (configured directly on the node). */
    modifyPrompt?: string;
    modifyReferenceUrl?: string;
    /** Live-update the Modify inputs (prompt / reference) from the node. */
    onModifyChange?: (patch: { prompt?: string; referenceUrl?: string }) => void;
    /** Upload a reference image → returns its URL. */
    onUploadReference?: (file: File) => Promise<string>;
    /** Change which tool the node uses. Clears other tools' settings. */
    onToolChange: (tool: EditTool) => void;
    /** Configure the current tool's settings (opens the modal). */
    onOpenSettings: () => void;
    /** Open the fabric design editor for this node's source image. */
    onOpenDesign?: () => void;
    /** Fire the generation. Disabled until settings exist. */
    onGenerate: () => void;
    onDrag: (deltaX: number, deltaY: number) => void;
    onClick?: (modifiers: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }) => void;
    onRemove?: () => void;
    /** Point the chat at this node - the vendor supplies the words. */
    onTargetInChat?: () => void;
    onDuplicate?: () => void;
    /** Faded / non-interactive rendering in workspace select mode. */
    dimmed?: boolean;
}

/**
 * Canvas node for image editing tools. The tool is chosen from a dropdown
 * at the top of the node (Expand / Upscale / Edit). Each tool has its own
 * settings flow — Expand opens a modal, the others ship later — and its
 * own Generate dispatch. The node sits between a source generation and
 * its edited result on the canvas.
 */
export const EditImageNode: React.FC<EditImageNodeProps> = ({
    nodeId,
    x,
    y,
    zoom,
    selected,
    tool,
    sourceImageUrl,
    settingsSummary,
    generating,
    creditCost,
    collapsed,
    onToggleCollapse,
    creditBalance,
    retouchRegions,
    upscaleScale,
    onUpscaleScaleChange,
    modifyPrompt,
    modifyReferenceUrl,
    onModifyChange,
    onUploadReference,
    onToolChange,
    onOpenSettings,
    onOpenDesign,
    onGenerate,
    onDrag,
    onClick,
    onRemove,
    onTargetInChat,
    onDuplicate,
    dimmed,
}) => {
    const { t } = useTranslation();
    const TOOL_META = useToolMeta();
    const toolMeta = TOOL_META[tool];
    const hasSettings = !!settingsSummary;
    // Modify + Upscale are configured inline on the node (no settings dialog).
    const inlineModify = tool === 'modify';
    const inlineUpscale = tool === 'upscale';
    const isDesign = tool === 'design';
    // Design opens the fabric design editor; the other dialog tools open the
    // shared image-editor modal. Modify / Upscale configure inline (no dialog).
    const openHandler = isDesign
        ? onOpenDesign
        : (toolMeta.ready && !inlineModify && !inlineUpscale ? onOpenSettings : undefined);
    const canOpenSettings = !!openHandler;
    // Retouch lists its regions (selections) under the preview.
    const showRetouchList = tool === 'retouch' && (retouchRegions?.length ?? 0) > 0;
    const canAfford =
        creditBalance == null || creditBalance === undefined || creditBalance >= creditCost;
    // Upscale needs no configuration — it can generate as soon as it's ready.
    const canGenerate = !generating && toolMeta.ready && canAfford && (inlineUpscale || hasSettings);

    const nodeActions = (
        <div className="flex gap-1.5">
            {onDuplicate && (
                <CircleActionButton title={t('CONTENT_STUDIO.ACTION_DUPLICATE')} icon={<SvgDuplicate />} onClick={onDuplicate} />
            )}
            {onTargetInChat && (
                <CircleActionButton
                    title={t('CONTENT_STUDIO.ACTION_ASK_AGENT', 'Ask the agent about this') as string}
                    icon={<SvgAgent />}
                    onClick={onTargetInChat}
                />
            )}
            {onRemove && (
                <CircleActionButton title={t('CONTENT_STUDIO.ACTION_REMOVE')} icon={<SvgRemove />} onClick={onRemove} destructive />
            )}
        </div>
    );

    // Folded: the tool it runs and the one button that matters. Nothing in this
    // node's body has to stay mounted for it to work — a design is flattened by
    // the canvas, not here — so unlike the recipe card there is no state that
    // needs the body rendered.
    if (collapsed) {
        return (
            <NodeShell
                nodeId={nodeId}
                x={x}
                y={y}
                width={EDIT_IMAGE_NODE_WIDTH}
                zoom={zoom}
                selected={selected}
                accent="var(--foreground)"
                dimmed={dimmed}
                label={t('CONTENT_STUDIO.NODE_EDIT_IMAGE')}
                onDrag={onDrag}
                onClick={onClick}
                pulse={generating}
                addHandle
                bottomAffordance={nodeActions}
            >
                <div className="flex flex-col gap-2.5 px-4 py-3.5">
                    <button
                        type="button"
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); onToggleCollapse?.(); }}
                        className="flex w-full items-center justify-between gap-2 text-start text-sm text-muted-foreground transition-colors hover:text-foreground"
                    >
                        <span className="min-w-0 truncate">
                            {toolMeta.label}
                            {settingsSummary && (
                                <span className="text-muted-foreground/70">{` · ${settingsSummary}`}</span>
                            )}
                        </span>
                        <ChevronDown className="size-3.5 shrink-0" />
                    </button>

                    <GenerateButton
                        generating={generating}
                        canGenerate={canGenerate}
                        creditCost={creditCost}
                        onGenerate={onGenerate}
                    />
                </div>
            </NodeShell>
        );
    }

    // The source preview is a button when a tool has a dialog to open, and inert
    // markup otherwise — so its contents are built once and mounted into either.
    const previewBody = (
        <>
            {sourceImageUrl ? (
                <img
                    src={sourceImageUrl}
                    alt="Source"
                    className={cn(
                        'absolute inset-0 size-full object-cover',
                        !toolMeta.ready && 'opacity-50',
                    )}
                />
            ) : (
                <span className="text-muted-foreground opacity-50 [&_svg]:size-8">
                    {TOOL_ICONS[tool]}
                </span>
            )}

            {/* Configured summary — small chip, top-left (non-inline tools;
                Retouch shows its regions as a list below instead). */}
            {!inlineModify && !inlineUpscale && !showRetouchList && hasSettings && settingsSummary && (
                <span className="absolute start-2 top-2 max-w-[calc(100%-16px)] truncate bg-foreground/80 px-1.5 py-0.5 text-[10.5px] font-medium tabular-nums text-background">
                    {settingsSummary}
                </span>
            )}

            {/* Floating action pill — tool name (like "Re-frame"). Modify /
                Upscale configure inline below, so they show no pill. */}
            {!inlineModify && !inlineUpscale && (
                <span className="absolute bottom-2.5 left-1/2 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-lg border bg-background/90 px-2.5 py-1 text-xs font-medium whitespace-nowrap text-foreground backdrop-blur-sm transition-colors group-hover/preview:border-input [&_svg]:size-3.5">
                    {toolMeta.ready ? TOOL_ICONS[tool] : null}
                    {toolMeta.ready
                        ? (hasSettings ? `${toolMeta.label} · ${t('CONTENT_STUDIO.ACTION_EDIT')}` : toolMeta.label)
                        : `${toolMeta.label} — ${t('CONTENT_STUDIO.COMING_SOON')}`}
                </span>
            )}
        </>
    );

    const previewClass = cn(
        'group/preview relative flex aspect-[16/10] w-full items-center justify-center overflow-hidden rounded-lg border bg-muted p-0 transition-colors',
        canOpenSettings ? 'cursor-pointer hover:border-input' : 'cursor-default',
    );

    return (
        <NodeShell
            nodeId={nodeId}
            x={x}
            y={y}
            width={EDIT_IMAGE_NODE_WIDTH}
            zoom={zoom}
            selected={selected}
            accent="var(--foreground)"
            dimmed={dimmed}
            label={t('CONTENT_STUDIO.NODE_EDIT_IMAGE')}
            onDrag={onDrag}
            onClick={onClick}
            pulse={generating}
            addHandle
            // Duplicate / Remove as circular buttons on node hover (bottom) —
            // same affordance as the recipe / product / generation nodes.
            bottomAffordance={nodeActions}
        >
            {/* Body — tool switcher + contextual settings card */}
            <div className="flex flex-col gap-2.5 p-4 pb-3">
                <SegmentedToolControl tool={tool} onChange={onToolChange} />

                {/* Preview card — the source image. For Expand/Retouch it's a
                    button with a floating action pill (opens the dialog, like the
                    3D studio's "Re-frame"); for Modify it's a plain preview with
                    the controls inline below; Upscale is dimmed + coming-soon. */}
                {canOpenSettings ? (
                    <button
                        type="button"
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); openHandler?.(); }}
                        className={previewClass}
                    >
                        {previewBody}
                    </button>
                ) : (
                    <div
                        onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                        className={previewClass}
                    >
                        {previewBody}
                    </div>
                )}

                {/* Modify — inline prompt + reference, right under the preview. */}
                {inlineModify && (
                    <ModifyInlineControls
                        prompt={modifyPrompt || ''}
                        referenceUrl={modifyReferenceUrl}
                        onChange={(patch) => onModifyChange?.(patch)}
                        onUpload={onUploadReference}
                    />
                )}

                {/* Upscale — inline scale selector (no dialog). */}
                {inlineUpscale && (
                    <div onMouseDown={(e) => e.stopPropagation()} className="flex items-center gap-2">
                        <span className="flex-1 text-xs text-muted-foreground">
                            {t('CONTENT_STUDIO.UPSCALE_HINT', 'Higher resolution + detail')}
                        </span>
                        <div className="flex gap-1 rounded-lg border bg-muted p-1">
                            {[2, 4].map((s) => {
                                const active = (upscaleScale || 2) === s;
                                return (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); onUpscaleScaleChange?.(s); }}
                                        className={cn(
                                            'rounded-md px-2.5 py-1 text-sm font-medium transition-colors hover:text-foreground',
                                            active ? 'bg-background text-foreground' : 'text-muted-foreground',
                                        )}
                                    >
                                        {s}×
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Retouch — list the drawn selections (region + its comment)
                    under the preview. Click any to reopen the editor. */}
                {showRetouchList && (
                    <div className="flex flex-col gap-1">
                        {retouchRegions!.map((r, i) => (
                            <button
                                key={r.id}
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onOpenSettings(); }}
                                className="flex w-full items-center gap-2 rounded-xl border bg-card px-2 py-1.5 text-start transition-colors hover:border-input hover:bg-muted"
                            >
                                <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-foreground text-[11px] font-medium text-background">
                                    {i + 1}
                                </span>
                                <span
                                    className={cn(
                                        'min-w-0 flex-1 truncate text-xs',
                                        r.prompt.trim() ? 'text-foreground' : 'text-muted-foreground italic',
                                    )}
                                >
                                    {r.prompt.trim() || t('CONTENT_STUDIO.RETOUCH_REGION_PLACEHOLDER')}
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Footer — outlined Generate (BorderBeam when ready), matching the
                recipe node. Neutral, no filled-black. */}
            <div className="border-t bg-muted px-4 py-3">
                {canGenerate ? (
                    <BorderBeam size="line" colorVariant="ocean" duration={2.4} strength={0.66}>
                        <GenerateButton
                            generating={generating}
                            canGenerate={canGenerate}
                            creditCost={creditCost}
                            onGenerate={onGenerate}
                            withIcon
                        />
                    </BorderBeam>
                ) : (
                    <GenerateButton
                        generating={generating}
                        canGenerate={canGenerate}
                        creditCost={creditCost}
                        onGenerate={onGenerate}
                        withIcon
                    />
                )}

                {!canAfford && !generating && toolMeta.ready && (
                    <p className="mt-2 text-center text-xs text-destructive">
                        {t('CONTENT_STUDIO.NOT_ENOUGH_CREDITS_WITH_BALANCE', { balance: creditBalance ?? 0 })}
                    </p>
                )}
            </div>
        </NodeShell>
    );
};

// The same Generate button serves the folded pill and the footer, so it lives
// here rather than being written twice with one glyph between them.
const GenerateButton: React.FC<{
    generating?: boolean;
    canGenerate: boolean;
    creditCost: number;
    onGenerate: () => void;
    /** Footer variant: play glyph, and the cost only when there is one. */
    withIcon?: boolean;
}> = ({ generating, canGenerate, creditCost, onGenerate, withIcon }) => {
    const { t } = useTranslation();
    return (
        <Button
            variant="outline"
            onClick={(e: React.MouseEvent) => { e.stopPropagation(); if (canGenerate) onGenerate(); }}
            className={cn(
                'h-auto w-full gap-2 px-4 py-2.5 text-sm',
                !canGenerate && 'cursor-not-allowed text-muted-foreground',
                !canGenerate && !generating && 'opacity-70',
            )}
        >
            {generating ? (
                <>
                    <Loader2 className="size-3.5 animate-spin" />
                    {t('CONTENT_STUDIO.GENERATING_BTN', 'Generating…')}
                </>
            ) : withIcon ? (
                <>
                    {/* Same play glyph the recipe node uses. */}
                    <Play className="size-4" />
                    {t('CONTENT_STUDIO.GENERATE', 'Generate')}{creditCost > 0 ? ` · ${creditCost} cr` : ''}
                </>
            ) : (
                <>{t('CONTENT_STUDIO.GENERATE', 'Generate')} · {creditCost} cr</>
            )}
        </Button>
    );
};

// ═══════════════════════════════════════════════════════════════════════
// Tool switcher — a compact segmented control (icon + short label per
// segment) inside a soft grey track. The active segment lifts to the card
// surface so the selection reads at a glance. Not-ready tools stay dim +
// not-allowed.
// ═══════════════════════════════════════════════════════════════════════

interface ToolSquareRowProps {
    tool: EditTool;
    onChange: (t: EditTool) => void;
}

// ═══════════════════════════════════════════════════════════════════════
// Modify — inline controls (prompt + optional reference image) shown right
// on the node under the preview, instead of a separate dialog.
// ═══════════════════════════════════════════════════════════════════════
const ModifyInlineControls: React.FC<{
    prompt: string;
    referenceUrl?: string;
    onChange: (patch: { prompt?: string; referenceUrl?: string }) => void;
    onUpload?: (file: File) => Promise<string>;
}> = ({ prompt, referenceUrl, onChange, onUpload }) => {
    // A 52×40 thumbnail cannot answer "is this the lighting I meant?", which is
    // the only reason the reference is there.
    const [previewOpen, setPreviewOpen] = useState(false);
    const { t } = useTranslation();
    const fileRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);

    const doUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !onUpload) return;
        setUploading(true);
        try { const url = await onUpload(file); onChange({ referenceUrl: url }); }
        catch { /* surfaced elsewhere */ }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
    };

    return (
        <div onMouseDown={(e) => e.stopPropagation()} className="flex flex-col gap-2">
            <Textarea
                value={prompt}
                onChange={(e) => onChange({ prompt: e.target.value })}
                placeholder={t('CONTENT_STUDIO.MODIFY_PROMPT_PLACEHOLDER')}
                rows={3}
                className="resize-y text-sm"
            />
            <ImagePreviewDialog
                url={previewOpen ? (referenceUrl || null) : null}
                alt={t('CONTENT_STUDIO.MODIFY_REFERENCE_IMAGE') as string}
                onClose={() => setPreviewOpen(false)}
            />
            {referenceUrl ? (
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setPreviewOpen(true)}
                        title={t('CONTENT_STUDIO.MODIFY_REFERENCE_VIEW', 'View reference') as string}
                        aria-label={t('CONTENT_STUDIO.MODIFY_REFERENCE_VIEW', 'View reference') as string}
                        className="h-10 w-[52px] shrink-0 cursor-zoom-in rounded-lg border bg-muted bg-cover bg-center p-0 transition-colors hover:border-foreground"
                        style={{ backgroundImage: `url(${referenceUrl})` }}
                    />
                    <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                        {t('CONTENT_STUDIO.MODIFY_REFERENCE_IMAGE')}
                    </span>
                    <Button
                        variant="outline"
                        size="xs"
                        onClick={() => onChange({ referenceUrl: undefined })}
                        className="shrink-0 text-muted-foreground hover:border-destructive/40 hover:text-destructive"
                    >
                        {t('CONTENT_STUDIO.ACTION_REMOVE')}
                    </Button>
                </div>
            ) : (
                <button
                    type="button"
                    disabled={!onUpload || uploading}
                    onClick={() => fileRef.current?.click()}
                    className={cn(
                        'flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-input bg-muted py-2 text-xs font-medium text-muted-foreground transition-colors',
                        onUpload ? 'cursor-pointer hover:border-foreground hover:text-foreground' : 'cursor-not-allowed',
                    )}
                >
                    {uploading ? t('CONTENT_STUDIO.MODIFY_UPLOADING') : `+ ${t('CONTENT_STUDIO.MODIFY_REFERENCE_IMAGE')}`}
                </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={doUpload} className="hidden" />
        </div>
    );
};

const TOOL_ICONS: Record<EditTool, React.ReactNode> = {
    expand: <Expand />,
    retouch: <SquareDashedMousePointer />,
    modify: <Pencil />,
    graphics: <Type />,
    upscale: <ImageUpscale />,
    design: <Brush />,
};

const SegmentedToolControl: React.FC<ToolSquareRowProps> = ({ tool, onChange }) => {
    const TOOL_META = useToolMeta();
    return (
        <div
            onMouseDown={(e) => e.stopPropagation()}
            className="flex gap-1 rounded-lg border bg-muted p-1"
        >
            {TOOL_OPTIONS.map((optId) => {
                const meta = TOOL_META[optId];
                const isActive = optId === tool;
                const disabled = !meta.ready;
                return (
                    <button
                        key={optId}
                        type="button"
                        disabled={disabled}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!disabled) onChange(optId);
                        }}
                        title={meta.hint}
                        className={cn(
                            'flex min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-md px-1 py-2 transition-colors [&_svg]:size-[18px]',
                            isActive
                                ? 'bg-background text-foreground'
                                : disabled
                                ? 'cursor-not-allowed text-muted-foreground opacity-55'
                                : 'text-muted-foreground hover:text-foreground',
                        )}
                    >
                        <span className="flex size-[18px] items-center justify-center">
                            {TOOL_ICONS[optId]}
                        </span>
                        <span className="max-w-full text-[9px] leading-[1.1] font-medium tracking-[0.02em] whitespace-nowrap uppercase">
                            {meta.shortLabel}
                        </span>
                    </button>
                );
            })}

        </div>
    );
};
